import type { ActionForm } from './action-form'
import type { ImprovementAction } from '~/gql/quality/quality'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { actionForm, isActionFormValid } from './action-form'
import { ACTION_STATUS_KEYS, ORIGIN_KEYS } from './quality-labels'
import QualityField, { FIELD_CLASS } from './QualityField'

type IProps = {
  /** L'action à suivre ; absente, on en crée une. */
  action?: ImprovementAction
  timeZone?: string | null
  formatDate: (value: unknown) => string
  busy: boolean
  onCancel: () => void
  onSave: (form: ActionForm) => void
}

/** Créer ou suivre une action d'amélioration (#328) : d'où elle vient, qui la porte, ce qu'elle a changé. */
function ImprovementActionDialog({ action, timeZone, formatDate, busy, onCancel, onSave }: IProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState(() => actionForm(action, timeZone))
  const set = <K extends keyof ActionForm>(key: K, value: ActionForm[K]) => setForm(current => ({ ...current, [key]: value }))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isActionFormValid(form)) onSave(form)
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{action ? action.title : t('quality.actions.new.title')}</DialogTitle>
            <DialogDescription>{action ? t('quality.actions.createdOn', { date: formatDate(action.createdAt) }) : t('quality.actions.new.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <QualityField id="actionOrigin" label={t('quality.actions.column.origin')} required>
              <select id="actionOrigin" required value={form.origin} onChange={e => set('origin', e.target.value as ActionForm['origin'])} className={`h-10 ${FIELD_CLASS}`}>
                <option value="" disabled>{t('quality.actions.originPlaceholder')}</option>
                {Object.entries(ORIGIN_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
              </select>
            </QualityField>
            {action && (
              <QualityField id="actionStatus" label={t('quality.column.status')}>
                <select id="actionStatus" value={form.status} onChange={e => set('status', e.target.value as ActionForm['status'])} className={`h-10 ${FIELD_CLASS}`}>
                  {Object.entries(ACTION_STATUS_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
                </select>
              </QualityField>
            )}
          </div>
          <QualityField id="actionTitle" label={t('quality.column.title')} required>
            <Input id="actionTitle" required maxLength={200} value={form.title} onChange={e => set('title', e.target.value)} />
          </QualityField>
          <QualityField id="actionDescription" label={t('quality.actions.column.description')} required help={t('quality.actions.descriptionHelp')}>
            <textarea id="actionDescription" required rows={3} maxLength={5000} value={form.description} onChange={e => set('description', e.target.value)} aria-describedby="actionDescriptionHelp" className={`py-2 ${FIELD_CLASS}`} />
          </QualityField>
          <div className="grid gap-4 sm:grid-cols-2">
            <QualityField id="actionOwner" label={t('quality.column.owner')}>
              <Input id="actionOwner" maxLength={150} value={form.owner} onChange={e => set('owner', e.target.value)} />
            </QualityField>
            <QualityField id="actionDueDate" label={t('quality.actions.column.dueDate')}>
              <Input id="actionDueDate" type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
            </QualityField>
          </div>
          {action && (
            <QualityField id="actionEffect" label={t('quality.actions.column.effect')} help={t('quality.actions.effectHelp')}>
              <textarea id="actionEffect" rows={3} maxLength={5000} value={form.effect} onChange={e => set('effect', e.target.value)} aria-describedby="actionEffectHelp" className={`py-2 ${FIELD_CLASS}`} />
            </QualityField>
          )}
          {action?.doneAt && <p className="text-xs text-muted-foreground">{t('quality.actions.doneOn', { date: formatDate(action.doneAt) })}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!isActionFormValid(form) || busy}>{action ? t('common.save') : t('quality.actions.new.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ImprovementActionDialog
